import { elementContains } from '@uifabric/utilities'

import { DEFAULT_SURFACE_ID } from '../constants'
import {
  FabricDependencies,
  IImageStyles,
  ITeachingBubble,
  ITeachingBubbleProps,
} from '../fabric-dependencies'
import { generateId, isIE } from '../utils'

import { SurfaceProps } from './office-web-surface-container'

const isInIE = isIE()
const wideModeMarginLeft = isInIE ? '15px' : '10px'

export const getTeachingBubbleProps = (
  fabricDependencies: FabricDependencies,
  props: SurfaceProps
): ITeachingBubbleProps => ({
  ariaDescribedBy: `${generateId()}-TD`,
  ariaLabelledBy: `${generateId()}-TL`,
  calloutProps: {
    directionalHint:
      props.anchorHint && fabricDependencies.DirectionalHint[props.anchorHint],
    id: DEFAULT_SURFACE_ID,
    preventDismissOnLostFocus: props.preventAutoDismiss ?? !props.focused,
    setInitialFocus: props.focused,
  },
  closeButtonAriaLabel: props.closeButtonAriaLabel ?? 'Close',
  focusTrapZoneProps: {
    disableFirstFocus: !props.focused,
    forceFocusInsideTrap: false,
  },
  footerContent: props.footer,
  hasCloseIcon: props.hasCloseButton ?? true,
  hasSmallHeadline: props.smallHeadline ?? false,
  headline: props.title,
  illustrationImage: {
    'aria-label': props.imageAriaLabel,
    onLoadingStateChange: props.onImageLoaded,
    src: props.illustrationImage,
    styles: isInIE ? getImageStyles() : undefined,
  },
  isWide: isLeftPositioned(props),
  onDismiss: props.onCalloutDismiss,
  primaryButtonProps: {
    ariaDescription: props.primaryButtonAriaDescription,
    ariaLabel: props.primaryButtonText,
    onClick: props.onPrimaryButtonClick,
    text: props.primaryButtonText,
  },
  secondaryButtonProps: props.secondaryButtonText
    ? {
        ariaDescription: props.secondaryButtonAriaDescription,
        ariaLabel: props.secondaryButtonText,
        onClick: props.onSecondaryButtonClick,
        text: props.secondaryButtonText,
      }
    : undefined,
  styles: {
    content: {
      marginLeft: isLeftPositioned(props) ? wideModeMarginLeft : undefined,
    },
  },
  target: props.targetElement,
})

export const getTeachingSurface = (fabricDependencies: FabricDependencies) => (
  props: SurfaceProps
) => {
  const {
    createElement: h,
    TeachingBubble,
    useEffect,
    useRef,
  } = fabricDependencies

  const teachingRef = useRef<ITeachingBubble>()

  useEffect(() => {
    const onFocusEvent = (focusEvent: Event) => {
      if (
        elementContains(
          // tslint:disable-next-line: no-any no-unsafe-any
          ((teachingRef.current as any)?.rootElement
            ?.current as HTMLDivElement) ?? null,
          focusEvent.target as HTMLElement
        )
      ) {
        props.onFocused()
      }
    }

    document.addEventListener('click', onFocusEvent)
    document.addEventListener('focus', onFocusEvent)

    return () => {
      document.removeEventListener('click', onFocusEvent)
      document.removeEventListener('focus', onFocusEvent)
    }
  }, [])

  return (
    <div>
      <div
        role='alert'
        style={{
          height: 0,
          opacity: 0,
          pointerEvents: 'none',
          position: 'fixed',
          width: 0,
        }}
      >
        {props.alertAriaText || props.title}
      </div>
      <TeachingBubble
        {...getTeachingBubbleProps(fabricDependencies, props)}
        // tslint:disable-next-line: no-any no-unsafe-any
        componentRef={teachingRef as any}
      >
        {props.description}
      </TeachingBubble>
    </div>
  )
}

const isLeftPositioned = (props: SurfaceProps) =>
  props && props.position === 'left'

const getImageStyles = (): Partial<IImageStyles> => {
  return {
    image: {
      objectFit: 'contain',

      height: '100%',
      width: '100%',
    },
  }
}
